"use client";

import * as React from "react";
import { ArrowRight, CircleCheck as CheckCircle2, CircleAlert as AlertCircle, Loader as Loader2, PhoneCall, FolderGit2, Users } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
const relatedLinks = [{
  title: "Contact",
  description: "Get in touch with our team for questions, consultations, and site visits.",
  to: "/ContactPage",
  icon: PhoneCall
}, {
  title: "Projects",
  description: "Explore our portfolio of kitchen, bath, and full-home renovations in Decatur.",
  to: "/ProjectsPage",
  icon: FolderGit2
}, {
  title: "About Us",
  description: "Learn about our craftsmanship standards, local heritage, and dedicated team.",
  to: "/AboutPage",
  icon: Users
}];
export default function RelatedLinksEstimateForm() {
  const [formData, setFormData] = React.useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    project_type: "",
    details: "",
    contact_method: "",
    timing: "",
    budget: "",
    consent: false
  });
  const [isLoading, setIsLoading] = React.useState(false);
  const [isSuccess, setIsSuccess] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSelectChange = (name: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleConsentChange = (checked: boolean | "indeterminate") => {
    setFormData(prev => ({
      ...prev,
      consent: checked === true
    }));
  };
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrorMessage(null);
    if (!formData.consent) {
      setErrorMessage("Please agree to the contact consent before submitting.");
      return;
    }
    if (!formData.project_type) {
      setErrorMessage("Please select a project type.");
      return;
    }
    if (!formData.contact_method) {
      setErrorMessage("Please select your preferred contact method.");
      return;
    }
    if (!formData.timing) {
      setErrorMessage("Please select your project timing.");
      return;
    }
    setIsLoading(true);
    try {
      const currentPath = typeof window !== "undefined" ? window.location.pathname : "";
      const searchParams = new URLSearchParams();
      searchParams.append("name", formData.name);
      searchParams.append("phone", formData.phone);
      searchParams.append("email", formData.email);
      searchParams.append("address", formData.address);
      searchParams.append("project_type", formData.project_type);
      searchParams.append("details", formData.details);
      searchParams.append("contact_method", formData.contact_method);
      searchParams.append("timing", formData.timing);
      searchParams.append("budget", formData.budget);
      searchParams.append("consent", formData.consent ? "true" : "false");
      searchParams.append("website", "DecaturHomeRenovation.com");
      searchParams.append("page", currentPath);
      searchParams.append("lead_source", "organic");
      const response = await fetch("https://hooks.zapier.com/hooks/catch/28361868/4mcz4d7/", {
        method: "POST",
        body: searchParams
      });
      if (response.ok) {
        setIsSuccess(true);
      } else {
        setErrorMessage("Something went wrong. Please call (470) 847-9076 instead.");
      }
    } catch {
      setErrorMessage("Something went wrong. Please call (470) 847-9076 instead.");
    } finally {
      setIsLoading(false);
    }
  };
  return <section id="relatedlinksestimateform" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-t border-border">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* Related Links Navigation Grid */}
        <div className="mb-16">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <h2 className="text-3xl md:text-4xl font-serif font-semibold tracking-tight text-foreground">
              Continue Exploring
            </h2>
            <p className="mt-2 text-muted-foreground text-sm md:text-base">
              Discover our previous work, read about our process, or reach out directly.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {relatedLinks.map((link, index) => {
            const IconComp = link.icon;
            return <Card key={index} data-index={index} className="group relative overflow-hidden transition-all duration-200 hover:border-foreground/30 hover:shadow-md bg-card/70 border-border/80">
                  <CardHeader className="pb-3">
                    <div className="size-10 rounded-lg bg-secondary flex items-center justify-center text-foreground mb-2 group-hover:scale-105 transition-transform">
                      <IconComp className="size-5" />
                    </div>
                    <CardTitle className="text-xl font-serif font-semibold text-foreground">
                      {link.title}
                    </CardTitle>
                    <CardDescription className="text-sm text-muted-foreground leading-relaxed pt-1">
                      {link.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <Button asChild variant="ghost" className="p-0 h-auto font-medium text-foreground group-hover:text-foreground/80 hover:bg-transparent">
                      <Link to={link.to} className="inline-flex items-center gap-1.5 text-sm">
                        <span>Visit {link.title}</span>
                        <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                      </Link>
                    </Button>
                  </CardContent>
                </Card>;
          })}
          </div>
        </div>

        <Separator className="my-12 opacity-60" />

        {/* Universal Estimate Form Section */}
        <div className="max-w-3xl mx-auto">
          <Card className="border-border bg-card/90 shadow-lg rounded-xl overflow-hidden">
            <CardHeader className="text-center space-y-2 pb-6 border-b border-border/60 bg-muted/20">
              <CardTitle className="text-2xl md:text-3xl font-serif font-semibold text-foreground">
                Request an Estimate
              </CardTitle>
              <CardDescription className="text-sm md:text-base text-muted-foreground max-w-xl mx-auto">
                Tell us about your upcoming project and our team will prepare a detailed consultation.
              </CardDescription>
            </CardHeader>

            <CardContent className="p-6 md:p-8">
              {isSuccess ? <div className="py-12 text-center space-y-4">
                  <div className="mx-auto size-14 rounded-full bg-secondary flex items-center justify-center text-foreground">
                    <CheckCircle2 className="size-8 text-foreground" />
                  </div>
                  <h3 className="text-2xl font-serif font-semibold text-foreground">
                    Request Received
                  </h3>
                  <p className="text-muted-foreground max-w-md mx-auto text-sm md:text-base">
                    Thank you for contacting Decatur Heritage Renovations. We have received your project details and will review them promptly to get in touch.
                  </p>
                </div> : <form onSubmit={handleSubmit} className="space-y-6">
                  {errorMessage && <Alert variant="destructive">
                      <AlertCircle className="size-4" />
                      <AlertTitle>Notice</AlertTitle>
                      <AlertDescription>{errorMessage}</AlertDescription>
                    </Alert>}

                  {/* Contact Details */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <Label htmlFor="estimate-name">
                        Full Name <span className="text-destructive">*</span>
                      </Label>
                      <Input id="estimate-name" name="name" type="text" placeholder="Jane Doe" required value={formData.name} onChange={handleInputChange} disabled={isLoading} />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="estimate-phone">
                        Phone Number <span className="text-destructive">*</span>
                      </Label>
                      <Input id="estimate-phone" name="phone" type="tel" placeholder="Your phone number" required value={formData.phone} onChange={handleInputChange} disabled={isLoading} />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <Label htmlFor="estimate-email">
                        Email Address <span className="text-destructive">*</span>
                      </Label>
                      <Input id="estimate-email" name="email" type="email" placeholder="Your email address" required value={formData.email} onChange={handleInputChange} disabled={isLoading} />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="estimate-address">
                        Street Address or ZIP Code <span className="text-destructive">*</span>
                      </Label>
                      <Input id="estimate-address" name="address" type="text" placeholder="Decatur, GA 30030" required value={formData.address} onChange={handleInputChange} disabled={isLoading} />
                    </div>
                  </div>

                  {/* Project Details Dropdowns */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <Label htmlFor="estimate-project-type">
                        Project Type <span className="text-destructive">*</span>
                      </Label>
                      <Select value={formData.project_type} onValueChange={val => handleSelectChange("project_type", val)} disabled={isLoading}>
                        <SelectTrigger id="estimate-project-type" className="w-full">
                          <SelectValue placeholder="Select project type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Kitchen Remodeling">Kitchen Remodeling</SelectItem>
                          <SelectItem value="Bathroom Remodeling">Bathroom Remodeling</SelectItem>
                          <SelectItem value="Whole-Home Remodeling">Whole-Home Remodeling</SelectItem>
                          <SelectItem value="Home Additions">Home Additions</SelectItem>
                          <SelectItem value="Basement Remodeling">Basement Remodeling</SelectItem>
                          <SelectItem value="Interior Remodeling">Interior Remodeling</SelectItem>
                          <SelectItem value="Outdoor Living">Outdoor Living</SelectItem>
                          <SelectItem value="Deck Remodeling">Deck Remodeling</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="estimate-contact-method">
                        Preferred Contact Method <span className="text-destructive">*</span>
                      </Label>
                      <Select value={formData.contact_method} onValueChange={val => handleSelectChange("contact_method", val)} disabled={isLoading}>
                        <SelectTrigger id="estimate-contact-method" className="w-full">
                          <SelectValue placeholder="Select contact preference" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Phone">Phone</SelectItem>
                          <SelectItem value="Text">Text</SelectItem>
                          <SelectItem value="Email">Email</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <Label htmlFor="estimate-timing">
                        Project Timing <span className="text-destructive">*</span>
                      </Label>
                      <Select value={formData.timing} onValueChange={val => handleSelectChange("timing", val)} disabled={isLoading}>
                        <SelectTrigger id="estimate-timing" className="w-full">
                          <SelectValue placeholder="When would you like to begin?" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ASAP">ASAP</SelectItem>
                          <SelectItem value="Within 1 month">Within 1 month</SelectItem>
                          <SelectItem value="1-3 months">1-3 months</SelectItem>
                          <SelectItem value="3-6 months">3-6 months</SelectItem>
                          <SelectItem value="Just researching">Just researching</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="estimate-budget">Estimated Budget</Label>
                      <Select value={formData.budget} onValueChange={val => handleSelectChange("budget", val)} disabled={isLoading}>
                        <SelectTrigger id="estimate-budget" className="w-full">
                          <SelectValue placeholder="Select estimated budget" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Under $25,000">Under $25,000</SelectItem>
                          <SelectItem value="$25,000-$50,000">$25,000-$50,000</SelectItem>
                          <SelectItem value="$50,000-$100,000">$50,000-$100,000</SelectItem>
                          <SelectItem value="$100,000+">$100,000+</SelectItem>
                          <SelectItem value="Not sure yet">Not sure yet</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Project Details Textarea */}
                  <div className="space-y-2">
                    <Label htmlFor="estimate-details">Project Details</Label>
                    <Textarea id="estimate-details" name="details" rows={4} placeholder="Share specifics about your room layout, desired materials, timeline constraints, or design goals." value={formData.details} onChange={handleInputChange} disabled={isLoading} />
                  </div>

                  {/* Consent Checkbox */}
                  <div className="flex items-start space-x-3 pt-2">
                    <Checkbox id="estimate-consent" checked={formData.consent} onCheckedChange={handleConsentChange} disabled={isLoading} required />
                    <div className="grid gap-1.5 leading-none">
                      <Label htmlFor="estimate-consent" className="text-xs md:text-sm font-normal text-muted-foreground cursor-pointer">
                        I agree to be contacted about my project by phone, text, or email.{" "}
                        <span className="text-destructive">*</span>
                      </Label>
                    </div>
                  </div>

                  {/* Submit Action */}
                  <div className="pt-3">
                    <Button type="submit" size="lg" className="w-full text-base font-medium transition-all" disabled={isLoading}>
                      {isLoading ? <>
                          <Loader2 className="size-4 animate-spin" />
                          <span>Submitting Request...</span>
                        </> : <span>Request Free Estimate</span>}
                    </Button>
                  </div>
                </form>}
            </CardContent>
          </Card>
        </div>
      </div>
    </section>;
}